import IconButton from "@mui/material/IconButton";
import ModalWrapper from "../Shared/Modal";
import CloseRounded from "@mui/icons-material/CloseRounded";
import Typography from "@mui/material/Typography";
import InputField from "../Shared/InputField";
import { useEffect, useState } from "react";
import { HelpOutlineRounded, VisibilityRounded } from "@mui/icons-material";
import { generateTimes, getNextTimeSlot, getTimezone, mapReduxTimeToString } from "./helpers";
import SelectOptions from "../Shared/SelectOptions";
import Checkbox from "@mui/material/Checkbox";
import classNames from "classnames";
import Button from "@mui/material/Button";
import LoadingButton from "../Shared/LoadingButton";
import dayjs from "dayjs";
import PreviewWarning from "./PreviewWarning";
import { useDispatch, useSelector } from "react-redux";
import { createTask, deleteTask, setShowTaskModal, updateTask } from "../../redux/slices/taskSlice";
import { AppDispatch, RootState } from "../../redux/store";
import SelectDate from "../Shared/SelectDate";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { Frequency, frequencyOptions, taskLimitByPlan, TaskType, WarningModalType, weekdayMap } from "./constants";
import DeleteModal from "../Shared/DeleteModal";
import useMediaQuery from "@mui/material/useMediaQuery";
import { theme } from "../../theme";
import Tooltip from "@mui/material/Tooltip/Tooltip";
import { showNotification } from "../../redux/slices/notificationSlice";
import WarningModal from "../Shared/WarningModal";

const getDefaultTaskDetails = () => ({
  name: "",
  user_query: "",
  cadence: Frequency.DAILY,
  should_send_email: true,
  time: getNextTimeSlot(30), // parsed from by_hour and by_minute
  by_week_day: dayjs().day() === 0 ? 7 : dayjs().day(), // backend expects 1–7
  by_month_day: dayjs().date(),
  date: dayjs(), // parsed from by_scheduled_time
});

const TaskModal = ({ countOfActiveTasks }: { countOfActiveTasks: number }) => {
  const [taskDetails, setTaskDetails] = useState(getDefaultTaskDetails());
  const [activeModal, setActiveModal] = useState<"PREVIEW" | "DELETE" | "UNSAVED">(null);

  const { showTaskModal } = useSelector((state: { tasks }) => state.tasks);
  const dispatch = useDispatch<AppDispatch>();

  const { loading, selectedTask, isDeleting } = useSelector((state: RootState) => state.tasks);
  dayjs.extend(customParseFormat);

  useEffect(() => {
    setActiveModal(null);
    if (selectedTask) {
      let taskDate = dayjs();

      if (selectedTask.cadence === Frequency.ONCE) {
        taskDate = dayjs()
          .year(selectedTask.by_year)
          .month(selectedTask.by_month - 1) // backend gives 1–12, dayjs needs 0–11
          .date(selectedTask.by_month_day)
          .hour(selectedTask.by_hour)
          .minute(selectedTask.by_minute);
      }

      const now = dayjs();
      let timeString = mapReduxTimeToString(selectedTask.by_hour, selectedTask.by_minute);
      let adjustedDate = taskDate;

      // If task is "once" and its scheduled datetime is in the past → normalize to the next valid time slot
      if (selectedTask.cadence === Frequency.ONCE && taskDate.isBefore(now)) {
        timeString = getNextTimeSlot(30);
        adjustedDate = now;
        if (dayjs(`${now.format("YYYY-MM-DD")} ${timeString}`).isBefore(now)) {
          adjustedDate = now.add(1, "day");
        }
      }
      setTaskDetails({
        name: selectedTask.name,
        user_query: selectedTask.user_query,
        cadence: selectedTask.cadence,
        time: timeString,
        should_send_email: selectedTask.should_send_email,
        by_week_day: selectedTask.by_week_day ?? (dayjs().day() === 0 ? 7 : dayjs().day()),
        by_month_day: selectedTask.by_month_day ?? dayjs().date(),
        date: adjustedDate,
      });
    }
    else {
      setTaskDetails(getDefaultTaskDetails());
    }
  }, [selectedTask, showTaskModal]);

  useEffect(() => {
    if (taskDetails.cadence === Frequency.ONCE) {
      const times = generateTimes(30, taskDetails.date);
      if (!times.some(t => t === taskDetails.time)) {
        setTaskDetails(prev => ({ ...prev, time: getNextTimeSlot(30) }));
      }
    }
  }, [taskDetails.cadence, taskDetails.date]);

  const buildUpdatedTask = () => {
    const base = {
      id: selectedTask?.id,
      name: taskDetails.name,
      user_query: taskDetails.user_query,
      cadence: taskDetails.cadence,
      should_send_email: taskDetails.should_send_email,
      timezone: getTimezone(),
    };

    const parsedTime = dayjs(taskDetails.time, "h:mm A");

    switch (taskDetails.cadence) {
      case Frequency.WEEKLY:
        return {
          ...base,
          by_week_day: taskDetails.by_week_day,
          by_hour: parsedTime.hour(),
          by_minute: parsedTime.minute(),
          by_month_day: null,
        };

      case Frequency.DAILY:
        return {
          ...base,
          by_hour: parsedTime.hour(),
          by_minute: parsedTime.minute(),
          by_week_day: null,
          by_month_day: null,
        };

      case Frequency.MONTHLY:
        return {
          ...base,
          by_month_day: taskDetails.by_month_day,
          by_hour: parsedTime.hour(),
          by_minute: parsedTime.minute(),
          by_week_day: null,
          by_scheduled_time: null,
        };

      case Frequency.ONCE:
        return {
          ...base,
          by_hour: parsedTime.hour(),
          by_minute: parsedTime.minute(),
          by_week_day: null,
          by_month_day: taskDetails.date.date(),
          by_month: taskDetails.date.month() + 1,
          by_year: taskDetails.date.year(),
        };

      default:
        return base;
    }
  };

  const plan = useSelector((state: { plan }) => state.plan?.plan);

  const handleSaveTask = async () => {
    const updatedTask: Partial<TaskType> = buildUpdatedTask();
    try {
      if (!selectedTask) {
        // enforce task limit based on plan
        if (taskLimitByPlan[plan.subscription_type] <= countOfActiveTasks) {
          dispatch(showNotification ({ title: "This task cannot be scheduled", message: `You’ve reached the limit of ${taskLimitByPlan[plan.subscription_type]} active tasks. Pause an active task to schedule this task.`, severity: "error", horizontal: "right" }));
          return;
        }
        await dispatch(createTask(updatedTask)).unwrap();
        dispatch(
          showNotification({
            title: "Task scheduled successfully",
            message: `You will be notified by email when your scheduled output is ready. Check your spam or junk folder if the email is not in your inbox.`,
            severity: "success",
            horizontal: "right",
          }),
        );
      }
      else {
        await dispatch(updateTask({ task: updatedTask })).unwrap();
        dispatch(
          showNotification({
            title: "Task updated successfully",
            message: `You will be notified by email when your scheduled output is ready. Check your spam or junk folder if the email is not in your inbox.`,
            severity: "success",
            horizontal: "right",
          }),
        );
      }
      dispatch(setShowTaskModal(false));
    }
    catch (err) {
      console.error(err);
      dispatch(showNotification ({ message: "Failed to create task, please try again.", severity: "error", horizontal: "right" }));
    }
  };
  const updateTaskField = (key: keyof typeof taskDetails) => (val) => {
    setTaskDetails(prev => ({ ...prev, [key]: val }));
  };

  const handleDeleteTask = async (taskId: string) => {
    await dispatch(deleteTask(taskId));
    setActiveModal(null);
    dispatch(setShowTaskModal(false));
  };

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleCloseTaskModal = () => {
    // show warning if there are unsaved changes
    if (selectedTask) {
      if (selectedTask.name === taskDetails.name && selectedTask.user_query === taskDetails.user_query && selectedTask.cadence === taskDetails.cadence) {
        dispatch(setShowTaskModal(false));
      }
      else {
        setActiveModal(WarningModalType.UNSAVED);
      }
    }
    else {
      dispatch(setShowTaskModal(false));
    }
  };

  return (
    <ModalWrapper
      open={showTaskModal}
      handleClose={() => {
        handleCloseTaskModal();
      }}
      wrapperClassName="md:w-modal-lg"
      className="flex flex-col gap-y-8 p-6 bg-white dark:bg-background/paper-elevation-16 overflow-y-auto max-h-[90vh]"
    >
      <>
        {activeModal === WarningModalType.DELETE && (
          <DeleteModal open={activeModal === WarningModalType.DELETE} handleDelete={() => handleDeleteTask(selectedTask?.id)} handleCloseWarning={() => setActiveModal(null)} mainText="Delete scheduled task?" subText="The associated chat will not be deleted, and this deletion cannot be reversed." isDeleting={isDeleting} />
        )}
        {activeModal === WarningModalType.PREVIEW && (
          <div className="flex flex-col gap-y-4">
            <PreviewWarning handleClose={() => setActiveModal(null)} queryToPreview={taskDetails.user_query} />
            <Typography variant="caption/regular-400" className="leading-[133%] text-text-disabled dark:text-mui-white-38">This preview may reflect incomplete or non-real-time data. The actual output may differ.</Typography>
          </div>
        )}
        {activeModal === WarningModalType.UNSAVED && (
          <WarningModal open={activeModal === WarningModalType.UNSAVED} handleExit={() => dispatch(setShowTaskModal(false))} handleCloseWarning={() => setActiveModal(null)} mainText="Your scheduled task has not been scheduled yet." subText="Your changes will be lost if you close this window." warningType="Task Scheduler" />
        )}
        {activeModal !== WarningModalType.PREVIEW && (
          <>
            <div className="flex justify-between">
              <Typography variant="h6/medium-500">Task Scheduler</Typography>
              <div className="flex gap-x-2">
                { !isMobile && (
                  <Button
                    variant="outlined"
                    color="secondary"
                    size="medium"
                    className={classNames(
                      " flex gap-x-2 !border-tgpt-secondary-states-outlinedBorder !border-none !text-transform-none !text-tgpt-secondary-main",
                      "disabled:dark:!text-mui-white-38",
                      "disabled:!text-mui-black-38",
                    )}
                    onClick={() => setActiveModal(WarningModalType.PREVIEW)}
                    disabled={!taskDetails.name || !taskDetails.user_query}
                  >
                    Preview Task
                    <VisibilityRounded className="!w-4 !h-4 !fill-tgpt-secondary-main"></VisibilityRounded>
                  </Button>
                )}
                <IconButton className="!h-8 !w-8" onClick={() => handleCloseTaskModal()}>
                  <CloseRounded className=" text-mui-black-38 dark:text-mui-white-38" />
                </IconButton>
              </div>
            </div>
            <form className="flex flex-col gap-y-4 w-full">
              <InputField value={taskDetails.name} setter={updateTaskField("name")} label="Name" showLabel={true} placeholder="Name this task" />
              <InputField value={taskDetails.user_query} setter={updateTaskField("user_query")} label="Prompt" placeholder="Prompts for the scheduled task" showLabel={true} multiline={true} rows={4} />
              <div className="flex items-center">
                <Typography variant="body1/regular-400">Schedule Frequency</Typography>
                <Tooltip title="Tasks will be scheduled in your local timezone" placement="right">
                  <IconButton>
                    <HelpOutlineRounded className="!fill-action-active !w-4 !h-4"></HelpOutlineRounded>
                  </IconButton>
                </Tooltip>
              </div>
              <section className="flex flex-col gap-y-2 sm:flex-row gap-x-4">
                <SelectOptions
                  value={taskDetails.cadence}
                  options={frequencyOptions}
                  handleChange={updateTaskField("cadence")}
                />
                {/* Conditional dropdowns */}
                {taskDetails.cadence === Frequency.DAILY && (
                  <SelectOptions
                    value={taskDetails.time}
                    options={generateTimes(30)}
                    handleChange={updateTaskField("time")}
                  />
                )}
                {(taskDetails.cadence === Frequency.WEEKLY)
                && (
                  <>
                    <SelectOptions
                      value={String(taskDetails.by_week_day)}
                      options={Object.entries(weekdayMap).map(([value, label]) => ({
                        value,
                        label,
                      }))}
                      handleChange={(val: string) => {
                        updateTaskField("by_week_day")(parseInt(val, 10));
                      }}
                    />
                    <SelectOptions
                      value={taskDetails.time}
                      options={generateTimes(30)}
                      handleChange={updateTaskField("time")}
                    />
                  </>
                )}

                {taskDetails.cadence === Frequency.ONCE && (
                  <>
                    <SelectDate
                      value={taskDetails.date}
                      setValue={updateTaskField("date")}
                    />
                    <SelectOptions
                      value={taskDetails.time}
                      options={generateTimes(30, taskDetails.date)}
                      handleChange={updateTaskField("time")}
                    />
                  </>
                )}
                {taskDetails.cadence === Frequency.MONTHLY && (
                  <>
                    <SelectOptions
                      value={String(taskDetails.by_month_day)}
                      options={Array.from({ length: 31 }, (_, index) => String(index + 1))}
                      handleChange={(val: string) => {
                        updateTaskField("by_month_day")(parseInt(val, 10));
                      }}
                    />
                    <SelectOptions
                      value={taskDetails.time}
                      options={generateTimes(30)}
                      handleChange={updateTaskField("time")}
                    />
                  </>
                )}
              </section>
              {isMobile && (
                <Button
                  className="!border-elevation-outlined !rounded-2xl !text-transform-none !text-tgpt-secondary-main disabled:!text-mui-black-38 flex gap-x-2"
                  variant="outlined"
                  onClick={() => setActiveModal(WarningModalType.PREVIEW)}
                  disabled={!taskDetails.name || !taskDetails.user_query}
                >
                  Preview Task
                  <VisibilityRounded className="!w-4 !h-4 !fill-tgpt-secondary-main"></VisibilityRounded>
                </Button>
              )}
              <div className="flex items-center gap-x-2">
                <Checkbox
                  className={
                    classNames("!p-0 text-mui-black-60 dark:text-mui-white-70",
                      {
                        "!text-tgpt-secondary-main": taskDetails.should_send_email,
                      },
                    )
                  }
                  checked={taskDetails.should_send_email}
                  onChange={() => setTaskDetails(prev => ({ ...prev, should_send_email: !prev.should_send_email }))}
                />
                <Typography variant="body2/regular-400" className="text-mui-black-60 dark:text-mui-white-70">Send me an email when my task output is ready</Typography>
              </div>
            </form>
            <div className="flex gap-x-4">
              {selectedTask
                ? (
                    <Button
                      variant="outlined"
                      className="!border-error-states-outlinedBorder hover:!bg-error-states-hover !normal-case !text-error-main"
                      onClick={() => setActiveModal(WarningModalType.DELETE)}
                      size="medium"
                      fullWidth
                    >
                      Delete
                    </Button>
                  )
                : (
                    <Button
                      variant="outlined"
                      className="!border-tgpt-secondary-states-outlinedBorder hover:bg-tgpt-secondary-states-hover !normal-case !text-tgpt-secondary-main"
                      onClick={() => dispatch(setShowTaskModal(false))}
                      size="medium"
                      fullWidth
                    >
                      Cancel
                    </Button>
                  )}
              <LoadingButton
                isLoading={loading}
                onClick={handleSaveTask}
                variant="contained"
                size="medium"
                fullWidth
                isDisabled={(!taskDetails.name || !taskDetails.user_query)}
                className="whitespace-nowrap"
              >
                Save
              </LoadingButton>
            </div>
          </>
        )}
      </>
    </ModalWrapper>
  );
};

export default TaskModal;
